import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { differenceInDays } from 'date-fns'

import LinkButton from '@components/LinkButton'

import { useGetTeamSubscription } from '@queries/Billing'
import useGetAccount from '@helpers/hooks/useGetAccount'
import { parseServerDate } from '@helpers/utils'
import { useGetQuestionnaireAnswers } from '@queries/Teams'
import routes from '@src/Routes/routes'
// import { useGetContent } from '@pages/Onboarding/api'

import styles from '../../index.module.css'

export interface TrialCounterProps {}

export const TrialCounter: React.FC<TrialCounterProps> = () => {
  const { t } = useTranslation('common', { keyPrefix: 'trialCounter' })
  const user = useGetAccount()
  const { data: subscription } = useGetTeamSubscription({ enabled: !!user?.team?.subscription })
  // Answers only feed the trial branch's demo link; don't fetch them for Free.
  const { data: answers } = useGetQuestionnaireAnswers(!!subscription?.is_trial)

  // const [isPatternsContentSet, setIsPatternsContentSet] = useState(false)
  // const [hasPatternsContent, setHasPatternsContent] = useState(true)
  // const patternsContentEnabled = !!subscription?.is_trial && !!user?.team?.onboarded

  // const { data: patternsContent } = useGetContent(
  //   user?.team?.id,
  //   patternsContentEnabled && !isPatternsContentSet && hasPatternsContent,
  // )

  const isTrial = !!subscription?.is_trial

  // trial_end arrives naive UTC (USE_TZ=False). Read as local time it lands
  // hours late in the Americas, and the floor + 1 below then said "8 days"
  // on day 0 of a 7-day trial.
  const trialEnd = parseServerDate(subscription?.trial_end)
  // null when the server sent no usable date: the countdown is then left
  // out rather than shown as "1 day left".
  const daysLeft = trialEnd ? Math.max(differenceInDays(trialEnd, new Date()) + 1, 0) : null
  const demoPath = useMemo(() => {
    // Trial-only CTA; an unset or malformed VITE_INAPP_DEMO_LINK must degrade
    // to no button, not throw during render.
    if (!isTrial || !import.meta.env.VITE_INAPP_DEMO_LINK) return ''
    try {
      const demoUrl = new URL(import.meta.env.VITE_INAPP_DEMO_LINK)
      demoUrl.searchParams.set('firstname', user?.name?.split(' ')?.[0] || '')
      demoUrl.searchParams.set('lastname', user?.name?.split(' ')?.[1] || '')
      demoUrl.searchParams.set('email', user?.email || '')
      demoUrl.searchParams.set('company', user?.team?.name || '')
      const phone = answers?.find((answer) => answer[1].startsWith('+'))?.[1]
      demoUrl.searchParams.set('phone', phone || '')

      return demoUrl.toString()
    } catch {
      return ''
    }
  }, [isTrial, user, answers])
  // const patternsLink = useMemo(() => {
  //   const demoPath = new URL(`https://approveit.today/trial`)
  //   demoPath.searchParams.set('pattern', patternsContent?.pattern || '')
  //   demoPath.searchParams.set('firstname', user?.name?.split(' ')?.[0] || '')
  //   demoPath.searchParams.set('lastname', user?.name?.split(' ')?.[1] || '')
  //   demoPath.searchParams.set('email', user?.email || '')
  //   // demoPath.searchParams.set('company', user?.team?.name || '')
  //   // const phone = answers?.find((answer) => answer[1].startsWith('+'))?.[1]
  //   // demoPath.searchParams.set('phone', phone || '')

  //   return demoPath.toString()
  // }, [patternsContent, user, answers])

  // useEffect(() => {
  //   if (hasPatternsContent && !patternsContent?.team) {
  //     setHasPatternsContent(false)
  //   } else if (!hasPatternsContent && !!patternsContent?.team) {
  //     setHasPatternsContent(true)
  //   }

  //   if (patternsContentEnabled && patternsContent?.pattern && !isPatternsContentSet) {
  //     setIsPatternsContentSet(true)
  //     window?.Intercom?.('update', {
  //       personal_landing_link: patternsLink,
  //     })
  //   }
  // }, [patternsContent, subscription, user, isPatternsContentSet, hasPatternsContent])

  // const isOfferLoading = hasPatternsContent && !isPatternsContentSet

  // Tri-state: undefined means the query hasn't resolved, not Free. Rendering
  // the Free nudge here would flash wrong copy while loading.
  if (!subscription) {
    return null
  }

  // Free plan (trial over or never started): ambient upgrade nudge instead of
  // the countdown. Paying plans render nothing (gated in Navigation).
  if (!subscription.is_trial) {
    return (
      <div className="h-12 text-sm sm:text-lg bg-violet-700 text-white flex items-center justify-center gap-2">
        <p>{subscription?.trial_eligible ? t('freeUpgradeMessageTrial') : t('freeUpgradeMessage')}</p>
        <LinkButton
          to={routes.private.billing}
          color="primary"
          type="outlined"
          className="text-sm sm:text-base px-1 sm:px-4"
        >
          {subscription?.trial_eligible ? t('freeUpgradeCtaTrial') : t('freeUpgradeCta')}
        </LinkButton>
      </div>
    )
  }

  return (
    <div className="h-12 text-sm sm:text-lg bg-violet-700 text-white flex items-center justify-center gap-2">
      {daysLeft !== null && (
        <p>
          <Trans
            i18nKey={subscription?.has_payment_method ? 'trialLeftMessageCard' : 'trialLeftMessage'}
            count={daysLeft}
            t={t}
            components={[<span className={styles.trialCounter} key={0} />]}
          />
        </p>
      )}
      {demoPath && (
        <LinkButton
          to={demoPath}
          reloadDocument
          target="_blank"
          color="primary"
          type="outlined"
          className="text-sm sm:text-base px-1 sm:px-4"
        >
          {t('setupCallLabel')}
        </LinkButton>
      )}
      {/* <LinkButton
        to={patternsLink}
        reloadDocument
        target="_blank"
        color="primary"
        type="outlined"
        className="text-sm sm:text-base px-1 sm:px-4"
        disabled={isOfferLoading}
      >
        {isOfferLoading ? (
          <div className="flex gap-3 items-center">
            {t('loadingLabel')}
            <div className="flex gap-1 justify-center items-center">
              <div className="h-1 w-1 bg-black rounded-full animate-bounce [animation-delay:-0.3s]" />
              <div className="h-1 w-1 bg-black rounded-full animate-bounce [animation-delay:-0.15s]" />
              <div className="h-1 w-1 bg-black rounded-full animate-bounce" />
            </div>
          </div>
        ) : (
          t('helpLabel')
        )}
      </LinkButton> */}
    </div>
  )
}

export default TrialCounter
